import { normalize } from 'normalizrplus';
import * as InvitationActions from '@/store/actions/Invitation.actions';
import * as InvitationMutations from '@/store/mutations/Invitation.mutations';
import * as DatabaseService from '@/services/Database.service';
import * as AuthMutations from '@/store/mutations/Auth.mutations';
// import * as UserActions from '@/store/actions/User.actions';
import * as UserMutations from '@/store/mutations/User.mutations';
// import * as WorkspaceActions from '@/store/actions/Workspace.actions';
import * as WorkspaceMutations from '@/store/mutations/Workspace.mutations';
import * as api from '@/services/Api.service';
import * as schemas from '@/store/schemas';
import { dictionaryFromDocumentArray } from '@/helpers/dataHelpers';
import { invitationStatusIds } from '@/constants/Invitation.constants';

const getInitialState = () => ({
  isWritingData: false,
  isFetchingData: false,
  invitations: {},
});

const receiveInvitations = (state, payload) => {
  state.invitations = {
    ...state.invitations,
    ...payload.invitations,
  };
  state.isFetchingData = false;
};

const state = getInitialState();

const getters = {
  pendingInvitations(state) {
    return Object.values(state.invitations)
      .filter(invitation => invitation.statusId === invitationStatusIds.PENDING)
      .reduce((acc, invitation) => {
        acc[invitation.id] = state.invitations[invitation.id];

        return acc;
      }, {});
  },
};

const mutations = {
  [InvitationMutations.INVITATION_BEGIN_WRITE_DATA](state) {
    state.isWritingData = true;
  },
  [InvitationMutations.INVITATION_COMPLETE_WRITE_DATA](state) {
    state.isWritingData = false;
  },
  [InvitationMutations.INVITATION_REQUEST_DATA](state) {
    state.isFetchingData = true;
  },
  [InvitationMutations.RECEIVE_INVITATIONS]: receiveInvitations,
  [UserMutations.RECEIVE_USERS]: receiveInvitations,
  [AuthMutations.LOG_OUT](state) {
    Object.assign(state, getInitialState());
  },
};

const actions = {
  async [InvitationActions.CREATE_INVITATION](context, payload) {
    context.commit(InvitationMutations.INVITATION_BEGIN_WRITE_DATA);
    try {
      const { docs: existingInvitations } = await DatabaseService.getPendingUserInvitationsByEmail(payload.newInvitation.inviteeEmail);

      if (existingInvitations.length) {
        throw new Error(`There's already a pending invitation for ${payload.newInvitation.inviteeEmail}`);
      }

      await DatabaseService.addNewInvitation(payload.newInvitation);
      context.commit(InvitationMutations.INVITATION_COMPLETE_WRITE_DATA);

      await context.dispatch(
        InvitationActions.FETCH_PENDING_WORKSPACE_INVITATIONS,
        { workspaceId: context.rootState.workspace.activeWorkspaceId },
      );
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
      throw new Error(err);
    }
  },
  async [InvitationActions.CREATE_INVITATIONS](context, payload) {
    context.commit(InvitationMutations.INVITATION_BEGIN_WRITE_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        await Promise.all(DatabaseService.addNewInvitations(payload.newInvitations));
        context.commit(InvitationMutations.INVITATION_COMPLETE_WRITE_DATA);

        await context.dispatch(
          InvitationActions.FETCH_PENDING_WORKSPACE_INVITATIONS,
          { workspaceId: context.rootState.workspace.activeWorkspaceId },
        );
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [InvitationActions.FETCH_WORKSPACE_INVITATIONS](context, payload) {
    context.commit(InvitationMutations.INVITATION_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: workspaceInvitations } = await DatabaseService.getWorkspaceInvitations(payload.workspaceId);
        const shapedWorkspaceInvitations = dictionaryFromDocumentArray(workspaceInvitations);

        context.commit(InvitationMutations.RECEIVE_INVITATIONS, { invitations: shapedWorkspaceInvitations });
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [InvitationActions.FETCH_PENDING_WORKSPACE_INVITATIONS](context, payload) {
    context.commit(InvitationMutations.INVITATION_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: workspaceInvitations } = await DatabaseService.getPendingWorkspaceInvitations(payload.workspaceId); // eslint-disable-line max-len
        const shapedWorkspaceInvitations = dictionaryFromDocumentArray(workspaceInvitations);

        context.commit(InvitationMutations.RECEIVE_INVITATIONS, { invitations: shapedWorkspaceInvitations });
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [InvitationActions.FETCH_PENDING_USER_INVITATIONS](context, payload) {
    context.commit(InvitationMutations.INVITATION_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: userInvitations } = await DatabaseService.getPendingUserInvitationsByEmail(payload.userEmail);
        const shapedUserInvitations = dictionaryFromDocumentArray(userInvitations);

        context.commit(InvitationMutations.RECEIVE_INVITATIONS, { invitations: shapedUserInvitations });
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [InvitationActions.FETCH_USER_INVITATIONS](context, payload) {
    context.commit(InvitationMutations.INVITATION_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: userInvitations } = await DatabaseService.getUserInvitationsByEmail(payload.userEmail);
        const shapedUserInvitations = dictionaryFromDocumentArray(userInvitations);

        context.commit(InvitationMutations.RECEIVE_INVITATIONS, { invitations: shapedUserInvitations });
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [InvitationActions.FETCH_INVITATION_BY_ID](context, payload) {
    context.commit(InvitationMutations.INVITATION_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const invitation = await DatabaseService.getInvitationById(payload.invitationId);
        if (invitation.exists) {
          const invitationDocument = { [invitation.id]: { id: invitation.id, ...invitation.data() } };
          context.commit(InvitationMutations.RECEIVE_INVITATIONS, { invitations: invitationDocument });
        } else {
          context.commit(InvitationMutations.RECEIVE_INVITATIONS, {});
        }
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [InvitationActions.FETCH_INVITATIONS_BY_TEAM_ID](context, payload) {
    context.commit(InvitationMutations.INVITATION_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { docs: userInvitations } = await DatabaseService.getInvitationsByTeamId(payload.teamId);
        const shapedTeamInvitations = dictionaryFromDocumentArray(userInvitations);

        context.commit(InvitationMutations.RECEIVE_INVITATIONS, { invitations: shapedTeamInvitations });
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },
  async [InvitationActions.ACCEPT_INVITATION](context, payload) {
    const {
      invitationId,
      workspaceId,
      newUser,
    } = payload;

    context.commit(InvitationMutations.INVITATION_BEGIN_WRITE_DATA);

    try {
      const { data: { user, invitation } } = await api.invitation.accept(invitationId, { newUser });
      const normalizedUser = normalize(user, schemas.user);
      const normalizedInvitation = normalize(invitation, schemas.invitation);

      context.commit(UserMutations.RECEIVE_USERS, normalizedUser.entities);
      context.commit(InvitationMutations.RECEIVE_INVITATIONS, normalizedInvitation.entities);
      context.commit(InvitationMutations.INVITATION_COMPLETE_WRITE_DATA);

      context.commit(
        WorkspaceMutations.SET_ACTIVE_WORKSPACE,
        { workspaceId },
      );
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
      // @TODO -- Set Error mutation;
    }
  },
  async [InvitationActions.VOID_INVITATION_BY_ID](context, payload) {
    const {
      invitationId,
    } = payload;

    context.commit(InvitationMutations.INVITATION_BEGIN_WRITE_DATA);

    return new Promise(async (resolve, reject) => {
      try {
        await DatabaseService.updateInvitationById(invitationId, { statusId: 3 });
        context.commit(InvitationMutations.INVITATION_COMPLETE_WRITE_DATA);
        context.dispatch(
          InvitationActions.FETCH_INVITATION_BY_ID,
          { invitationId },
        );
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        // @TODO -- Set Error mutation;
        reject(err);
      }
    });
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
